// ═══════════════════════════════════════════════════════════════
// AccountDeletionModal.jsx — Multi-step confirmation for self-delete
//
// Flow:
//   1. disclosure  — what's deleted vs retained, 7-year retention
//   2. typeConfirm — user must type "DELETE MY ACCOUNT" verbatim
//   3. progress    — calls deleteOwnAccount cloud function
//   4. success     — "Go to Homepage" → clear local state + reload
//   5. error       — shows server error, offers retry
//
// The cloud function does the heavy lifting (atomic transaction,
// Stripe cancel, Auth delete, confirmation email). See
// argumentor-functions/functions/index.js → deleteOwnAccount.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback } from 'react';
import { AlertTriangle, Loader2, X, CheckCircle, ArrowRight } from 'lucide-react';
import { STRIPE_CONFIG } from '../shared.jsx';
import ModalDialog from './ModalDialog.jsx';

const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

async function callDeleteOwnAccount({ userId, confirmPhrase }) {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = await window.firebaseAuth?.currentUser?.getIdToken(false);
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch (e) { /* proceed without auth */ }
  const response = await fetch(`${STRIPE_CONFIG.cloudFunctionUrl}/deleteOwnAccount`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ userId, confirmPhrase }),
  });
  return { status: response.status, body: await response.json().catch(() => ({})) };
}

function AccountDeletionModal({ user, onClose }) {
  const [step, setStep] = useState('disclosure'); // disclosure | typeConfirm | progress | success | error
  const [typedPhrase, setTypedPhrase] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const phraseMatches = typedPhrase === CONFIRM_PHRASE;

  const handleDelete = useCallback(async () => {
    setStep('progress');
    setErrorMessage('');
    try {
      const { status, body } = await callDeleteOwnAccount({
        userId: user.id,
        confirmPhrase: CONFIRM_PHRASE,
      });
      if (status === 200 && body?.success) {
        setStep('success');
      } else {
        setErrorMessage(body?.error || `Server returned ${status}. Please try again or contact info@4hci.com.`);
        setStep('error');
      }
    } catch (e) {
      setErrorMessage(e.message || 'Network error. Please check your connection and try again.');
      setStep('error');
    }
  }, [user.id]);

  const handleGoHome = useCallback(() => {
    try { localStorage.removeItem('argumentor-current-user'); } catch (e) { /* */ }
    try { localStorage.clear(); } catch (e) { /* */ }
    window.location.href = '/';
  }, []);

  const isInProgress = step === 'progress';

  return (
    <ModalDialog onClose={step !== 'progress' && step !== 'success' ? onClose : undefined} labelledBy="account-deletion-title" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                {step === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-700" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                )}
              </div>
              <h2 id="account-deletion-title" className="text-lg font-semibold text-stone-800">
                {step === 'success' ? 'Account Deleted' : 'Delete Your Account'}
              </h2>
            </div>
            {step !== 'progress' && step !== 'success' && (
              <button onClick={onClose} className="text-stone-500 hover:text-stone-600" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {step === 'disclosure' && (
            <div className="space-y-4 text-sm text-stone-700">
              <p className="font-medium text-stone-800">
                This action is permanent and cannot be undone. Please read carefully.
              </p>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="font-medium text-red-900 mb-2">Permanently deleted:</p>
                <ul className="text-xs text-red-800 space-y-1 list-disc pl-5">
                  <li>Your login credentials (you will no longer be able to sign in)</li>
                  <li>Your name, email, and avatar from active records</li>
                  <li>Your mood entries and push notification tokens</li>
                  <li>Your free-tier tool history (Say It Your Way, Mood Journal, Vote Mode)</li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="font-medium text-amber-900 mb-2">Retained for 7 years (legal hold):</p>
                <ul className="text-xs text-amber-800 space-y-1 list-disc pl-5">
                  <li>Conflict content you created or participated in (your identity becomes &quot;Deleted User&quot;)</li>
                  <li>A sealed legal-hold record that can only be consulted in response to valid legal process (e.g., subpoena in a family-law, custody, or civil proceeding)</li>
                </ul>
                <p className="text-xs text-amber-700 mt-2">
                  After 7 years, the sealed record and pseudonymized content are permanently destroyed. See Privacy Policy § 6.5 for details.
                </p>
              </div>

              <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                <p className="text-xs text-stone-700">
                  If you have an active subscription, it will be cancelled. Monthly plans remain active until the end of your current paid period. Annual plans are cancelled immediately with a prorated refund.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep('typeConfirm')}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 'typeConfirm' && (
            <div className="space-y-4 text-sm text-stone-700">
              <p>
                To confirm, type <span className="font-mono font-bold text-red-700">{CONFIRM_PHRASE}</span> in the box below exactly as shown.
              </p>
              <input
                type="text"
                value={typedPhrase}
                onChange={(e) => setTypedPhrase(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                autoFocus
                className="w-full px-4 py-3 border-2 border-stone-200 rounded-lg focus:outline-none focus:border-red-500 font-mono text-sm"
              />
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => { setTypedPhrase(''); setStep('disclosure'); }}
                  className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleDelete}
                  disabled={!phraseMatches}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-stone-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Delete My Account
                </button>
              </div>
            </div>
          )}

          {isInProgress && (
            <div className="py-8 flex flex-col items-center gap-3 text-stone-600">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
              <p className="text-sm">Deleting your account…</p>
              <p className="text-xs text-stone-500">This may take a few seconds. Please don&apos;t close this window.</p>
            </div>
          )}

          {step === 'success' && (
            <div className="space-y-4 text-sm text-stone-700">
              <p className="text-stone-800">
                Your account has been deleted successfully.
              </p>
              <p className="text-xs text-stone-600">
                A confirmation has been sent to your email. Your personal data and login credentials are gone. Conflict content you participated in remains pseudonymized for 7 years per our Privacy Policy § 6.5.
              </p>
              <p className="text-xs text-stone-600">
                Thank you for being part of The ArguMentor.
              </p>
              <button
                onClick={handleGoHome}
                className="w-full mt-2 py-3 bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-lg text-sm font-medium transition-colors"
              >
                Go to Homepage
              </button>
            </div>
          )}

          {step === 'error' && (
            <div className="space-y-4 text-sm text-stone-700">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="font-medium text-red-900 mb-1">Account deletion failed</p>
                <p className="text-xs text-red-800">{errorMessage}</p>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => { setStep('typeConfirm'); setErrorMessage(''); }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalDialog>
  );
}

export default AccountDeletionModal;
